import React, { useEffect, useState, useRef } from "react";
import { Box } from "@mui/material";
import { Tab, TabsNavigation } from "pepsico-ds";
import { useSelector } from "react-redux";
import TabData from "./TabData";
import CustomCuratorFilter from "../CustomCuratorFilter";
import { getCuratorApiListByPageName } from "../../../API/curatorApi";
import Loading from "../../../utils/Loader";
import { CURATOR_PAGE_API_CONSTANT } from "../../../constant/constant";
import { mergeUnique } from "../../../utils/function";
import useDebounce from "../../../hooks/useDebounce";

const defaultData = {
  documents: [],
  videos: [],
  links: [],
  documentCount: 0,
  videoCount: 0,
  linkCount: 0,
  totalCount: 0,
};

const AccordionView = ({ pageName }) => {
  const isDarkTheme = useSelector((state) => state.theme);
  const serachText = useSelector((state) => state.curatorPageLookUpData).search;
  const [tab, setTab] = useState(0);
  const handleClick = (index) => setTab(index);
  const [tabLoading, setTabLoading] = useState(true);
  const debouncedSearchText = useDebounce(serachText, 250);
  const pageDataRef = useRef({ ...defaultData });
  const [filteredRow, setFilteredRows] = useState({ ...defaultData });
  const [filterValue, setFilterValue] = useState({
    sector: null,
    country: null,
    subject: null,
    period: null,
  });

  const prepareTotalArray = (annualApprovalData) => {
    const totalArray = {
      documents: mergeUnique(
        annualApprovalData?.expiredContentReview?.documents,
        annualApprovalData?.ninetyDaysContentReview?.documents
      ),

      links: mergeUnique(
        annualApprovalData?.expiredContentReview?.links,
        annualApprovalData?.ninetyDaysContentReview?.links
      ),

      videos: mergeUnique(
        annualApprovalData?.expiredContentReview?.videos,
        annualApprovalData?.ninetyDaysContentReview?.videos
      ),
    };
    totalArray.documentCount = totalArray.documents.length;
    totalArray.linkCount = totalArray.links.length;
    totalArray.videoCount = totalArray.videos.length;
    totalArray.totalCount =
      totalArray.documentCount + totalArray.linkCount + totalArray.videoCount;
    return totalArray;
  };

  useEffect(() => {
    getCuratorApiListByPageName(pageName)
      .then((response) => {
        if (pageName === CURATOR_PAGE_API_CONSTANT.yearly_review) {
          const total = prepareTotalArray(response);
          pageDataRef.current = { total, ...response };
          setFilteredRows({ ...total });
        } else {
          pageDataRef.current = response;
          setFilteredRows(response);
        }
        setTabLoading(false);
      })
      .catch((error) => {
        setTabLoading(false);
      });
  }, []);

  useEffect(() => {
    const filteredRows = dataTransaformation();
    if (filterValue?.subject !== null) {
      filteredRows.documents = filteredRows.documents.filter((currentRow) =>
        currentRow?.subjects?.includes(filterValue?.subject)
      );
      filteredRows.documentCount = filteredRows.documents.length;

      filteredRows.links = filteredRows.links.filter((currentRow) =>
        currentRow?.subjects?.includes(filterValue?.subject)
      );
      filteredRows.linkCount = filteredRows.links.length;

      filteredRows.videos = filteredRows.videos.filter((currentRow) =>
        currentRow?.subjects?.includes(filterValue?.subject)
      );
      filteredRows.videoCount = filteredRows.videos.length;
    }

    if (filterValue?.sector !== null) {
      filteredRows.documents = filteredRows.documents.filter((currentRow) =>
        currentRow?.sector?.includes(filterValue?.sector)
      );
      filteredRows.documentCount = filteredRows.documents.length;

      filteredRows.links = filteredRows.links.filter((currentRow) =>
        currentRow?.sector?.includes(filterValue?.sector)
      );
      filteredRows.linkCount = filteredRows.links.length;

      filteredRows.videos = filteredRows.videos.filter((currentRow) =>
        currentRow?.sector?.includes(filterValue?.sector)
      );
      filteredRows.videoCount = filteredRows.videos.length;
    }
    if (filterValue?.country !== null) {
      filteredRows.documents = filteredRows.documents.filter((currentRow) =>
        currentRow?.country?.includes(filterValue?.country)
      );
      filteredRows.documentCount = filteredRows.documents.length;

      filteredRows.links = filteredRows.links.filter((currentRow) =>
        currentRow?.country?.includes(filterValue?.country)
      );
      filteredRows.linkCount = filteredRows.links.length;

      filteredRows.videos = filteredRows.videos.filter((currentRow) =>
        currentRow?.country?.includes(filterValue?.country)
      );
      filteredRows.videoCount = filteredRows.videos.length;
    }
    filteredRows.totalCount =
      filteredRows.videoCount +
      filteredRows.linkCount +
      filteredRows.documentCount;

    const searchedArray = searchRows(filteredRows, debouncedSearchText);
    setFilteredRows({ ...searchedArray });
  }, [filterValue, debouncedSearchText]);

  const searchRows = (filteredArray, search) => {
    if (search === "") return filteredArray;
    const searchedArray = { ...defaultData };
    searchedArray.documents = filteredArray?.documents.filter((item) =>
      Object?.values(item)?.some((value) =>
        String(value)
          ?.toLowerCase()
          ?.includes(search?.toLowerCase() || "")
      )
    );
    searchedArray.links = filteredArray?.links.filter((item) =>
      Object?.values(item)?.some((value) =>
        String(value)
          ?.toLowerCase()
          ?.includes(search?.toLowerCase() || "")
      )
    );
    searchedArray.videos = filteredArray?.videos.filter((item) =>
      Object?.values(item)?.some((value) =>
        String(value)
          ?.toLowerCase()
          ?.includes(search?.toLowerCase() || "")
      )
    );
    searchedArray.documentCount = searchedArray.documents.length;
    searchedArray.linkCount = searchedArray.links.length;
    searchedArray.videoCount = searchedArray.videos.length;
    searchedArray.totalCount =
      searchedArray.documentCount +
      searchedArray.linkCount +
      searchedArray.videoCount;
    return searchedArray;
  };

  const updatedFilterValue = (value, type) => {
    setFilterValue((preState) => {
      return {
        ...preState,
        [type]: value ?? null,
        ...(type === "sector" ? { country: null } : {}),
      };
    });
  };
  const dataTransaformation = () => {
    if (pageName === CURATOR_PAGE_API_CONSTANT.yearly_review) {
      switch (filterValue.period) {
        case "Next 30 days":
          return { ...pageDataRef.current.thirtyDaysContentReview };
        case "Next 60 days":
          return { ...pageDataRef.current.sixtyDaysContentReview };
        case "Next 90 days":
          return { ...pageDataRef.current.ninetyDaysContentReview };
        case "Past due":
          return { ...pageDataRef.current.expiredContentReview };
        default:
          return { ...pageDataRef.current.total } ?? { ...defaultData };
      }
    }
    return { ...pageDataRef.current };
  };
  // FUNCTION THAT RETURNS DATA BASED ON CURRENT TAB
  const getTabData = (currentTab, filteredRow) => {
    switch (currentTab) {
      case 0:
        return [
          ...filteredRow.documents,
          ...filteredRow.videos,
          ...filteredRow.links,
        ];
      case 1:
        return filteredRow.documents;
      case 2:
        return filteredRow.videos;
      case 3:
        return filteredRow.links;
      default:
        return [];
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "24px",
        paddingBottom: "16px",
      }}
    >
      <CustomCuratorFilter
        pageName={pageName}
        updatedFilterValue={updatedFilterValue}
      />
      {tabLoading ? (
        <Loading />
      ) : (
        <div className="flex flex-col gap-6">
          <TabsNavigation
            selectedIndex={tab}
            isDark={isDarkTheme}
          >
            <Tab
              iconTrailing={false}
              label={`All (${filteredRow?.totalCount ?? 0})`}
              onClick={() => handleClick(0)}
              orientation="horizontal"
              isDark={isDarkTheme}
              className="!text-[#616161]"
            />
            <Tab
              iconTrailing={false}
              label={`Documents (${filteredRow?.documentCount ?? 0})`}
              onClick={() => handleClick(1)}
              orientation="horizontal"
              isDark={isDarkTheme}
              className="!text-[#616161]"
            />
            <Tab
              label={`Videos (${filteredRow?.videoCount ?? 0})`}
              iconTrailing={false}
              onClick={() => handleClick(2)}
              isDark={isDarkTheme}
              className="!text-[#616161]"
            />
            <Tab
              label={`Links (${filteredRow?.linkCount ?? 0})`}
              iconTrailing={false}
              onClick={() => handleClick(3)}
              isDark={isDarkTheme}
              className="!text-[#616161]"
            />
          </TabsNavigation>
          {Object.keys(filteredRow).length > 0 && (
            <TabData
              currentTabData={getTabData(tab, filteredRow)}
              activeTab={tab}
              navPathUrl={
                pageName === CURATOR_PAGE_API_CONSTANT.yearly_review
                  ? "/curator/yearly-review"
                  : "/curator/pending-approval"
              }
            />
          )}
        </div>
      )}
    </Box>
  );
};

export default AccordionView;
